import { BenchmarkTypeOf } from "@/modules/benchmark/benchmarkTypes"
import { ChronoDtoSchema } from "@/modules/chrono/services/chronoDtos"
import { UserDtoSchema } from "@/modules/profile/userProfile/userDto"
import { ScoreResultType, ScoreType } from "@/modules/scores/scoreTypes"
import { ScoreboardScoreDtoSchema } from "@/modules/scores/scoreboard/gateway/scoreboardDto"
import { WebSessionDetailsDtoSchema } from "@/modules/sessions/gateway/programInfiniteSessionDto"
import {
  ExerciseInputDtoSchema,
  ScoreSetConfigurationDtoSchema,
  ScoreSetConfigurationInputDtoSchema,
} from "@/modules/sessions/gateway/sessionBlockDto"
import {
  ExerciseCategories,
  ProgramExerciseTypeOf,
  UserExerciseTypeOf,
} from "@/modules/sessions/sessionDetails/blocks/exerciseBlock/exerciseTypes"
import { SessionBlockType } from "@/modules/sessions/sessionDetails/sessionBlockTypes"
import { TemplateCategoryDtoSchema } from "@/modules/template/gateway/templateCategoryDto"
import { TemplateType } from "@/modules/template/types/templateType"
import { MAX_255_CHARACTERS, MAX_64_CHARACTERS, MAX_65535_CHARACTERS } from "@/typings/formik"
import { z } from "zod"

export const TemplateDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    templateCategory: TemplateCategoryDtoSchema.nullable(),
    creationDate: z.iso.datetime(),
    lastUpdateDate: z.iso.datetime(),
    templateType: z.enum(TemplateType),
  })
  .meta({ id: "TemplateDtoSchema" })

export const SessionExerciseDtoSchema = z
  .object({
    id: z.string(),
    orderIndex: z.number(),
    blockType: z.enum(SessionBlockType),
    title: z.string(),
    type: z.union([z.literal(ProgramExerciseTypeOf), z.literal(UserExerciseTypeOf), z.literal(BenchmarkTypeOf)]),
    description: z.string(),
    category: z.enum(ExerciseCategories),
    chrono: ChronoDtoSchema.nullable(),
    isFavorite: z.boolean(),
    owner: UserDtoSchema.nullable(),
    isCompleted: z.boolean(),
    score: ScoreboardScoreDtoSchema.nullable(),
    scoreType: z.enum(ScoreType), // deprecated
    scoringOption: z.enum(ScoreResultType).nullable(),
    scoreSetConfigurations: z.array(ScoreSetConfigurationDtoSchema),
    rpe: z.number().nullable(),
    requiredMedia: z.boolean(),
    durationInMin: z.number().nullable(),
    otherCategory: z.string().nullable(),
    scoreSubmissionDeadline: z.iso.datetime().nullable(),
  })
  .meta({ id: "SessionExerciseDtoSchema" })

export const ExerciseResponseDtoSchema = z
  .object({
    type: z.literal(TemplateType.EXERCISE),
    exercise: SessionExerciseDtoSchema,
  })
  .meta({ id: "ExerciseResponseDtoSchema" })

export const SessionResponseDtoSchema = z
  .object({
    type: z.literal(TemplateType.SESSION),
    session: WebSessionDetailsDtoSchema,
  })
  .meta({ id: "SessionResponseDtoSchema" })

export const WeekResponseDtoSchema = z
  .object({
    type: z.literal(TemplateType.WEEK),
    sessions: WebSessionDetailsDtoSchema.array(),
  })
  .meta({ id: "WeekResponseDtoSchema" })

export const TemplateElementDtoSchema = SessionResponseDtoSchema.or(WeekResponseDtoSchema)
  .or(ExerciseResponseDtoSchema)
  .meta({ id: "TemplateElementDtoSchema" })

export const TemplateSessionExerciseDtoSchema = z
  .object({
    title: z.string(),
    type: z.union([z.literal(ProgramExerciseTypeOf), z.literal(UserExerciseTypeOf), z.literal(BenchmarkTypeOf)]),
    description: z.string(),
    category: z.enum(ExerciseCategories),
    chrono: ChronoDtoSchema.nullable(),
    owner: UserDtoSchema.nullable(),
    scoreType: z.enum(ScoreType), // deprecated
    scoringOption: z.enum(ScoreResultType).nullable(),
    scoreSetConfigurations: z.array(ScoreSetConfigurationDtoSchema),
    rpe: z.number().nullable(),
    requiredMedia: z.boolean(),
    durationInMin: z.number().nullable(),
    otherCategory: z.string().nullable(),
  })
  .meta({ id: "TemplateSessionExerciseDtoSchema" })

export const EditExerciseTemplateInputDtoSchema = z
  .object({
    description: z.string().max(MAX_65535_CHARACTERS),
    category: z.enum(ExerciseCategories),
    otherCategory: z.string().nullable(),
    chrono: ChronoDtoSchema.nullable(),
    scoreType: z.string(), // deprecated
    scoringOption: z.enum(ScoreResultType).nullable(),
    scoreSetConfigurations: z.array(ScoreSetConfigurationInputDtoSchema).nullable(),
    rpe: z.number().nullable(),
    requiredMedia: z.boolean(),
    durationInMin: z.number().nullable(),
  })
  .meta({ id: "EditExerciseTemplateInputDtoSchema" })

export const EditTemplateInputDtoSchema = z
  .object({
    name: z.string().min(1).max(MAX_64_CHARACTERS),
    description: z.string().max(MAX_255_CHARACTERS).nullable(),
    templateCategoryId: z.string().nullable(),
  })
  .meta({ id: "EditTemplateInputDtoSchema" })

export const CreateExerciseTemplateInputDtoSchema = z
  .object({
    name: z.string().min(1).max(MAX_64_CHARACTERS),
    description: z.string().max(MAX_255_CHARACTERS).nullable(),
    templateCategoryId: z.string().nullable(),
    exerciseId: z.string().nullable(),
    exerciseContent: ExerciseInputDtoSchema.nullable(),
  })
  .refine(
    ({ exerciseId, exerciseContent }) =>
      (exerciseId === null && exerciseContent !== null) || (exerciseId !== null && exerciseContent === null),
    {
      message:
        "Either exerciseId must be null and exerciseContent non-null, or exerciseId non-null and exerciseContent null",
      path: ["exerciseId", "exerciseContent"],
    },
  )
  .meta({ id: "CreateExerciseTemplateInputDtoSchema" })

export const CreateSessionTemplateInputDtoSchema = z
  .object({
    name: z.string().min(1).max(MAX_64_CHARACTERS),
    description: z.string().max(MAX_255_CHARACTERS).nullable(),
    templateCategoryId: z.string().nullable(),
    sessionId: z.string(),
  })
  .meta({ id: "CreateSessionTemplateInputDtoSchema" })

export const CreateWeekTemplateInputDtoSchema = z
  .object({
    name: z.string().min(1).max(MAX_64_CHARACTERS),
    description: z.string().max(MAX_255_CHARACTERS).nullable(),
    templateCategoryId: z.string().nullable(),
    sessionIds: z.array(z.string()),
  })
  .meta({ id: "CreateWeekTemplateInputDtoSchema" })

export const ApplyExerciseTemplateInputDtoSchema = z
  .object({
    orderIndex: z.int32(),
  })
  .meta({ id: "ApplyExerciseTemplateInputDtoSchema" })

export const ApplySessionTemplateInputDtoSchema = z
  .object({
    programId: z.string(),
    date: z.iso.date().nullable(),
    index: z.int32().nullable(),
    virtualProgramByDateIndex: z.int32().nullable(),
  })
  .refine(({ date, index }) => (date === null && index !== null) || (date !== null && index === null), {
    message: "Either date must be null and index non-null, or date non-null and index null",
    path: ["date", "index"],
  })
  .meta({ id: "ApplySessionTemplateInputDtoSchema" })

export const ApplyWeekTemplateInputDtoSchema = z
  .object({
    programId: z.string(),
    startDate: z.iso.date().nullable(),
    startIndex: z.int32().nullable(),
    overwrite: z.boolean(),
  })
  .refine(
    ({ startDate, startIndex }) =>
      (startDate === null && startIndex !== null) || (startDate !== null && startIndex === null),
    {
      message: "Either startDate must be null and startIndex non-null, or startDate non-null and startIndex null",
      path: ["startDate", "startIndex"],
    },
  )
  .meta({ id: "ApplyWeekTemplateInputDtoSchema" })

export type ApplySessionTemplateInputDto = z.infer<typeof ApplySessionTemplateInputDtoSchema>
export type ApplyExerciseTemplateInputDto = z.infer<typeof ApplyExerciseTemplateInputDtoSchema>
export type ApplyWeekTemplateInputDto = z.infer<typeof ApplyWeekTemplateInputDtoSchema>
export type CreateExerciseTemplateInputDto = z.infer<typeof CreateExerciseTemplateInputDtoSchema>
export type CreateSessionTemplateInputDto = z.infer<typeof CreateSessionTemplateInputDtoSchema>
export type CreateWeekTemplateInputDto = z.infer<typeof CreateWeekTemplateInputDtoSchema>
export type EditExerciseTemplateInputDto = z.infer<typeof EditExerciseTemplateInputDtoSchema>
export type TemplateDto = z.infer<typeof TemplateDtoSchema>
export type TemplateElementDto = z.infer<typeof TemplateElementDtoSchema>
export type EditTemplateInputDto = z.infer<typeof EditTemplateInputDtoSchema>
export type TemplateSessionExerciseDto = z.infer<typeof TemplateSessionExerciseDtoSchema>
